import React, { useEffect, useMemo, useState } from 'react';
import classes from './SettingsList.module.css';
import IconButton from '../../UI/IconButton/IconButton';
import EditIcon from '../../UI/icons/EditIcon/EditIcon';
import TrashIcon from '../../UI/icons/TrashIcon/TrashIcon';
import Chip from '../../UI/Chip/Chip';
import Button from '../../UI/Button/Button';

type SettingsItem = {
    id: string;
    name?: string;
    products?: unknown[];
    productsCount?: number;
    discountedProductsCount?: number;
};
interface ISettingsListProps {
    items: SettingsItem[];
    onEdit: (id: string) => void;
    onDelete: (id: string) => void;
}

const collator = new Intl.Collator('ru', {
    usage: 'sort',
    sensitivity: 'base',
    numeric: true,
    ignorePunctuation: true,
});

const DEFAULT_VISIBLE_COUNT = 5;

const SettingsList: React.FC<ISettingsListProps> = ({ items, onEdit, onDelete }) => {
    const [showAll, setShowAll] = useState(false);

    const sortedItems = useMemo(() => {
        const withIndex = items.map((item, index) => ({ item, index }));
        withIndex.sort((a, b) => {
            const aNameRaw = (a.item.name ?? '').trim();
            const bNameRaw = (b.item.name ?? '').trim();
            const aEmpty = aNameRaw.length === 0;
            const bEmpty = bNameRaw.length === 0;
            if (aEmpty !== bEmpty) return aEmpty ? 1 : -1;
            const byName = collator.compare(aNameRaw, bNameRaw);
            if (byName !== 0) return byName;
            const byId = collator.compare(a.item.id, b.item.id);
            if (byId !== 0) return byId;
            return a.index - b.index;
        });
        return withIndex.map((x) => x.item);
    }, [items]);

    const shouldCollapse = sortedItems.length > DEFAULT_VISIBLE_COUNT;

    useEffect(() => {
        if (!shouldCollapse && showAll) setShowAll(false);
    }, [shouldCollapse, showAll]);

    const visibleItems = useMemo(() => {
        if (!shouldCollapse) return sortedItems;
        return showAll ? sortedItems : sortedItems.slice(0, DEFAULT_VISIBLE_COUNT);
    }, [sortedItems, shouldCollapse, showAll]);

    return (
        <div className={classes.wrapper}>
            <ul className={classes.list}>
                {visibleItems.map(({ id, name, products, productsCount, discountedProductsCount }) => {
                    const count = Array.isArray(products)
                        ? products.length
                        : typeof productsCount === 'number' && Number.isFinite(productsCount)
                          ? productsCount
                          : '-';
                    return (
                        <li className={classes['list-container']} key={id}>
                            <div className={classes['product-list-item']}>
                                {name}
                                <div className={classes.action}>
                                    <IconButton onClick={() => onEdit(id)}>
                                        <EditIcon />
                                    </IconButton>

                                    <IconButton onClick={() => onDelete(id)}>
                                        <TrashIcon fill={'lightgray'} />
                                    </IconButton>
                                </div>
                            </div>

                            <div className={classes['chip-wrapper']}>
                                <Chip text={`Товаров: ${count}`} />
                                {typeof discountedProductsCount === 'number' && discountedProductsCount > 0 && (
                                    <Chip text={`Со скидкой: ${discountedProductsCount}`} />
                                )}
                            </div>
                        </li>
                    );
                })}
            </ul>
            {shouldCollapse && !showAll && (
                <div className={classes.footer}>
                    <Button mode="secondary" onClick={() => setShowAll(true)} title="Показать все элементы">
                        Показать все
                    </Button>
                </div>
            )}
        </div>
    );
};

export default SettingsList;
